import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddUser() {
  const [username, setUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selectedLocation, setSelectedLocation] = useState([]); // Ensure it's an array
  const [locations, setLocations] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const [userRole, setUserRole] = useState("");
  const [generatedUserCode, setGeneratedUserCode] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user and initialize data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userData = response.data;
        setCurrentUser(userData);

        if (userData.role === "Main Admin") {
          fetchOrganizations();
        } else if (userData.role === "Company Admin") {
          fetchLocations();
          setSelectedOrganization(userData.organization);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/locations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const locationsWithId = response.data.locations.map((location) => ({
          ...location,
          id: location.location_code, // Map location_code to id
        }));

        setLocations(locationsWithId);
      } catch (error) {
        console.error("Error fetching locations:", error);
        toast.error("Error fetching locations. Please try again.");
      }
    };

    const fetchOrganizations = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/organizations`);
        setOrganizations(response.data.organizations);
      } catch (error) {
        console.error("Error fetching organizations:", error);
        toast.error("Failed to fetch organizations.");
      }
    };

    fetchCurrentUser();
  }, []); //  Added dependency array to prevent infinite re-renders

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fetch default location when organization changes
  useEffect(() => {
    if (currentUser?.role !== "Company Admin" && selectedOrganization) {
      fetchDefaultLocation();
    }
  }, [selectedOrganization, currentUser]); 

  const fetchDefaultLocation = async () => {
    if (!selectedOrganization) {
      setSelectedLocation([]); 
      return;
    }
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }
      const response = await axios.get(
        `${API_BASE_URL}/locations/default-location/${selectedOrganization}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { defaultLocation } = response.data;
      if (defaultLocation) {
        setSelectedLocation([defaultLocation._id]); 
      } else {
        toast.warning("No default location found for this organization.");
      }
    } catch (error) {
      console.error("Error fetching default location:", error);
      toast.error("Error fetching default location. Please try again.");
    }
  };

  // Handle form submission
  const handleAddUser = async () => {
    if (!username || !userEmail || !userPassword || !confirmPassword || !userRole) {
      toast.error("All fields are required.");
      return;
    }
    if (!userEmail || !isValidEmail(userEmail)) {
          toast.error("Please enter a valid email address.");
          return;
        }
    if (userPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    if (currentUser.role === "Main Admin" && !selectedOrganization) {
      toast.error("Organization is required.");
      return;
    }

    let assignedLocation = selectedLocation;

    //  Ensure Company Admin gets the default location automatically
    if (currentUser.role === "Main Admin" && userRole === "Company Admin") {
      if (!selectedLocation || selectedLocation.length === 0) {
        toast.error("Location is required for Company Admin.");
        return;
      }
      assignedLocation = selectedLocation;
    }

    const formData = {
      username,
      user_email: userEmail,
      password: userPassword,
      location: assignedLocation, // Ensure location is an array
      organization: currentUser.role === "Company Admin" ? currentUser.organization : selectedOrganization,
      organization_prefix: selectedOrganization?.organization_prefix, // Pass the organization prefix
      role: userRole,
    };

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/users/register`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const { message, user } = response.data;
      toast.success(message);
      setGeneratedUserCode(user.user_code);
    } catch (error) {
      console.error("Error adding user:", error);
      
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message); // Show the message from the backend (e.g., "Username already exists")
      } else {
        toast.error("Error adding user. Please try again.");
      }
    }
  };

  // Handle location selection for Company Admin
  const handleLocationSelection = (locationId) => {
    setSelectedLocation((prev) =>
      prev.includes(locationId) ? prev.filter((id) => id !== locationId) : [...prev, locationId]
    );
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add User</h2>
        <div className="grid grid-cols-2 gap-8">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">User Name</label>
            <input
              type="text"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter user name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">User Email</label>
            <input
              type="email"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter user email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Password</label>
            <input
              type="password"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter password"
              value={userPassword}
              onChange={(e) => setUserPassword(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          {currentUser?.role === "Main Admin" && (
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Organization</label>
              <select
                className="w-full p-3 border rounded-lg"
                value={selectedOrganization}
                onChange={(e) => setSelectedOrganization(e.target.value)}
              >
                <option value="" disabled>
                  Select Organization
                </option>
                {organizations.map((organization) => (
                  <option key={organization._id} value={organization._id}>
                    {organization.organization_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {currentUser?.role === "Company Admin" && (
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Locations</label>
              <div className="grid grid-cols-2 gap-4">
                {locations.map((location) => (
                  <label key={location._id} className="flex items-center">
                    <input
                      type="checkbox"
                      className="mr-2"
                      value={location._id}
                      checked={selectedLocation.includes(location._id)}
                      onChange={() => handleLocationSelection(location._id)}
                    />
                    {location.location_name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">User Role</label>
            <select
              className="w-full p-3 border rounded-lg"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
            >
              <option value="" disabled>
                Select User Role
              </option>
              {currentUser?.role === "Company Admin" && (
                <>
                  <option value="Farm User">Farm User</option>
                  <option value="Outlet User">Outlet User</option>
                  <option value="Accountant">Accountant</option>
                </>
              )}
              {currentUser?.role === "Main Admin" && (
                <option value="Company Admin">Company Admin</option>
              )}
            </select>
          </div>
        </div>

        {generatedUserCode && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <p className="text-green-700 font-bold">
              Generated User Code: {generatedUserCode}
            </p>
          </div>
        )}

        <div className="flex space-x-4 mt-6">
          <button
            className="px-6 py-3 bg-green-700 text-white rounded-lg hover:bg-green-700"
            onClick={handleAddUser}
          >
            Add User
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddUser;
